import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import type { MediaItemFormType } from "@/modules/common/media/mediaFormTypes"
import { MediaType } from "@/modules/common/media/mediaTypes"
import { cn } from "@/modules/lib/utils"
import { FileUploadImage } from "@/modules/ui/components/fileUpload/fileUploadImage"
import { ImageUploadIcon } from "@images/svgIcons/imageUploadIcon"
import { HUImageView } from "@modules/ui/components/huImageView"
import getCroppedImg from "@modules/utils/cropImage"
import { LoggerType } from "@modules/utils/loggerUtils"
import { Check, LoaderCircle, Pencil, Plus, TrashIcon, XIcon } from "lucide-react"
import { type CSSProperties, useMemo, useState } from "react"
import Cropper, { type Area } from "react-easy-crop"
import styled from "styled-components"

export type HUImageFormProps = {
  type: "logo" | "cover"
  image: MediaItemFormType | null
  setImage: (i?: MediaItemFormType) => void
  placeHolder?: {
    message: string
    style?: CSSProperties
  }
  renderEmpty?: React.ReactNode
  showPreviewAsCover?: boolean
  onStartUpload?: () => void
  onEndUpload?: () => void
  setIsImageUploading?: (isImageUploading: boolean) => void
  hideButton?: boolean
  size?: string
  acceptedTypes?: string
  resizer?: boolean
  className?: string
} & React.ComponentProps<"div">

export function HUImageForm({
  type,
  image,
  setImage,
  placeHolder,
  renderEmpty,
  showPreviewAsCover = false,
  onStartUpload,
  setIsImageUploading,
  onEndUpload,
  hideButton = false,
  size,
  acceptedTypes = "image/jpeg, image/png, image/bmp, image/webp, image/gif",
  resizer = false,
  className,
  ...props
}: HUImageFormProps) {
  const [loadingImage, setLoadingImage] = useState(false)
  const [edit, setEdit] = useState(false)
  const [rotation] = useState(0)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>({
    height: 0,
    width: 0,
    x: 0,
    y: 0,
  })

  const setCroppedImage = async () => {
    try {
      if (image?.file) {
        const croppedImage = await getCroppedImg(image.file, croppedAreaPixels)
        if (croppedImage) {
          setImage({
            url: "",
            file: croppedImage.newFile,
            key: null,
            type: MediaType.S3_IMAGE,
            filename: croppedImage.newFile.name,
            isUploading: true,
          })
        }
        setEdit(false)
      }
    } catch (e) {
      console.error(LoggerType.UI, "Error during cropping image", e)
    }
  }

  const urlImage = useMemo(() => {
    return image?.file ? URL.createObjectURL(image.file) : (image?.url ?? undefined)
  }, [image])

  return (
    <div className={cn("relative mt-2 mb-2 flex w-fit flex-1 flex-col items-center", className)} {...props}>
      <LogoImageContainer className={`${type}-container`} $size={size}>
        {image && !loadingImage ? (
          showPreviewAsCover ? (
            <div
              className="relative h-50 w-full overflow-hidden bg-cover bg-center"
              style={{
                backgroundImage: image.url ? `url(${image.url})` : undefined,
              }}
            ></div>
          ) : resizer && edit ? (
            <Cropper
              image={urlImage ?? ""}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              objectFit="cover"
              aspect={type === "logo" ? 4 / 4 : 4 / 3}
              cropShape={type === "logo" ? "round" : "rect"}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_: Area, croppedAreaPixels: Area) => {
                setCroppedAreaPixels(croppedAreaPixels)
              }}
            />
          ) : (
            <HUImageView
              image={urlImage ? { url: urlImage, hash: "" } : undefined}
              className={type}
              width={"100%"}
              height={"100%"}
            />
          )
        ) : (
          <PlaceHolderContainer className={type} style={placeHolder?.style}>
            {loadingImage ? (
              <LoaderCircle className="animate-spin" />
            ) : (
              <FileUploadImage
                setImage={setImage}
                onStartUpload={() => {
                  onStartUpload?.()
                  setLoadingImage(true)
                  setIsImageUploading?.(true)
                }}
                onEndUpload={() => {
                  onEndUpload?.()
                  setLoadingImage(false)
                  setIsImageUploading?.(false)
                }}
                className="h-full"
                acceptedTypes={acceptedTypes}
              >
                {renderEmpty ?? (
                  <div className="flex h-full items-center justify-center gap-2">
                    <ImageUploadIcon color="currentColor" />
                    {placeHolder && (
                      <div className="mt-2" style={{ textAlign: "center" }}>
                        <Text fontStyle="LL">{` ${placeHolder.message}`}</Text>
                      </div>
                    )}
                    {type === "logo" && !hideButton && (
                      <Button variant="outline" size="iconMd" className="absolute right-0 bottom-0 focus:shadow-none">
                        <Plus />
                      </Button>
                    )}
                  </div>
                )}
              </FileUploadImage>
            )}
          </PlaceHolderContainer>
        )}
      </LogoImageContainer>

      {!!image &&
        (edit ? (
          <Button
            variant="outline"
            size="iconMd"
            onClick={() => {
              setEdit(false)
            }}
            className="absolute top-0 right-0 z-20"
          >
            <XIcon />
          </Button>
        ) : (
          <Button
            variant="outline"
            size="iconMd"
            onClick={() => {
              setImage(undefined)
            }}
            className="absolute top-0 right-0 z-20"
          >
            <TrashIcon />
          </Button>
        ))}
      {resizer && urlImage && (
        <Button
          variant="outline"
          size="iconMd"
          onClick={() => {
            if (edit) {
              void setCroppedImage()
            } else {
              setEdit(true)
            }
          }}
          className="absolute right-0 bottom-2"
        >
          {edit ? <Check /> : <Pencil />}
        </Button>
      )}
    </div>
  )
}

const LogoImageContainer = styled.div<{ $size?: string }>`
  margin-bottom: 10px;

  &.logo-container {
    width: ${({ $size }) => $size ?? "150px"};
    height: ${({ $size }) => $size ?? "150px"};
    max-width: 100%;

    .logo {
      border-radius: 50%;
      width: 100%;
      height: 100%;
    }
  }

  &.cover-container {
    width: ${({ $size }) => $size ?? "100%"};
    height: ${({ $size }) => $size ?? "200px"};
    display: flex;
    justify-content: center;

    & div {
      border-radius: 20px;
    }
  }

  .cover {
    width: 100%;
    height: 100%;
    border-radius: 6px;
  }

  img.logo {
    object-fit: cover;
  }

  img.cover {
    object-fit: contain;
  }

  div.logo,
  div.cover {
    background-color: var(--neutral-50);
  }
`

const PlaceHolderContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
`
